import React, { useState, useEffect, useCallback, useContext } from 'react';
import apiClient from '../api';
import AuthContext from '../context/AuthContext';
import MedicalForm from './MedicalForm';

function MedicalInfo({ juvenileId }) {
  const [record, setRecord] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const { hasPermission } = useContext(AuthContext);

  const fetchRecord = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await apiClient.get(`/api/medical/juvenile/${juvenileId}`);
      setRecord(response.data);
    } catch (err) {
      setError('Failed to fetch medical records.');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  }, [juvenileId]);

  useEffect(() => {
    fetchRecord();
  }, [fetchRecord]);
  
  const handleFormSubmit = async (formData) => {
    try {
      const response = await apiClient.post(`/api/medical/juvenile/${juvenileId}`, formData);
      setRecord(response.data);
      setIsEditing(false);
    } catch (err) {
      console.error('Failed to save medical record:', err);
      alert('Failed to save medical record.');
    }
  };

  const renderDisplayView = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-slate-700">Allergies</h3>
        <p className="text-slate-600 whitespace-pre-wrap">{record?.allergies || 'None reported.'}</p>
      </div>
      <div>
        <h3 className="text-lg font-semibold text-slate-700">Current Medications</h3>
        <p className="text-slate-600 whitespace-pre-wrap">{record?.medications || 'None reported.'}</p>
      </div>
      <div>
        <h3 className="text-lg font-semibold text-slate-700">Ongoing Medical Conditions</h3>
        <p className="text-slate-600 whitespace-pre-wrap">{record?.medical_conditions || 'None reported.'}</p>
      </div>
      <div className="pt-4 border-t">
        <h3 className="text-lg font-semibold text-slate-700">Emergency Contact</h3>
        <p className="text-slate-600"><strong>Name:</strong> {record?.emergency_contact_name || 'N/A'}</p>
        <p className="text-slate-600"><strong>Phone:</strong> {record?.emergency_contact_phone || 'N/A'}</p>
      </div>
    </div>
  );

  return (
    <div className="card">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-bold text-slate-800 mb-0">Medical & Health Information</h2>
        {hasPermission('manage-medical-records') && !isEditing && (
          <button onClick={() => setIsEditing(true)} className="bg-gold text-white hover:bg-gold-dark font-bold py-2 px-4 rounded-lg shadow-sm">
            Edit
          </button>
        )}
      </div>

      {isLoading && <p>Loading medical records...</p>}
      {error && <p className="text-red-500">{error}</p>}
      
      {!isLoading && !error && (
        isEditing ? (
          <MedicalForm 
            initialData={record} 
            onSubmit={handleFormSubmit} 
            onCancel={() => setIsEditing(false)} 
          />
        ) : (
          renderDisplayView()
        )
      )}
    </div>
  );
}

export default MedicalInfo;